/**
 * Enhanced logging utility to help with debugging during migration
 */

// Log levels
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3
}

// Current log level - can be changed at runtime
let currentLogLevel = LogLevel.INFO;

// Backend types for logging
export enum BackendType {
  AMPLIFY = 'AMPLIFY',
  CDK = 'CDK'
}

/**
 * Set the current log level
 * @param level The log level to set
 */
export const setLogLevel = (level: LogLevel) => {
  currentLogLevel = level;
};

/**
 * Log a debug message
 * @param message The message to log
 * @param backend The backend type (optional)
 * @param data Additional data to log (optional)
 */
export const debug = (message: string, backend?: BackendType, data?: any) => {
  if (currentLogLevel <= LogLevel.DEBUG) {
    const backendPrefix = backend ? `[${backend}] ` : '';
    console.debug(`${backendPrefix}${message}`, data || '');
  }
};

/**
 * Log an info message
 * @param message The message to log
 * @param backend The backend type (optional)
 * @param data Additional data to log (optional)
 */
export const info = (message: string, backend?: BackendType, data?: any) => {
  if (currentLogLevel <= LogLevel.INFO) {
    const backendPrefix = backend ? `[${backend}] ` : '';
    console.info(`${backendPrefix}${message}`, data || '');
  }
};

/**
 * Log a warning message
 * @param message The message to log
 * @param backend The backend type (optional)
 * @param data Additional data to log (optional)
 */
export const warn = (message: string, backend?: BackendType, data?: any) => {
  if (currentLogLevel <= LogLevel.WARN) {
    const backendPrefix = backend ? `[${backend}] ` : '';
    console.warn(`${backendPrefix}${message}`, data || '');
  }
};

/**
 * Log an error message
 * @param message The message to log
 * @param backend The backend type (optional)
 * @param error The error object (optional)
 */
export const error = (message: string, backend?: BackendType, error?: any) => {
  if (currentLogLevel <= LogLevel.ERROR) {
    const backendPrefix = backend ? `[${backend}] ` : '';
    console.error(`${backendPrefix}${message}`, error || '');
  }
};

export default {
  setLogLevel,
  debug,
  info,
  warn,
  error,
  LogLevel,
  BackendType
};
